import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';

import { catalogApi, companyApi } from '@/api';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardHeader,
  Checkbox,
  ErrorState,
  EmptyState,
  Icon,
  Input,
  LoadingState,
  PageHeader,
  Select,
  Textarea,
  useToast,
} from '@/components/ui';
import { SkillsInput } from '@/features/internships/SkillsInput';
import { VerifyToContinueButton } from '@/features/verification/CompanyVerificationGate';
import { useCompanyVerification } from '@/features/verification/useCompanyVerification';
import { applyFieldErrors, errorMessage } from '@/hooks/useApiError';
import { usePermissions } from '@/hooks/usePermissions';
import { paths } from '@/routes/paths';
import { PERMISSIONS } from '@/types/roles';
import { internshipSchema } from '@/validations';

const WORK_MODES = [
  { value: 'ONSITE', label: 'On site' },
  { value: 'REMOTE', label: 'Remote' },
  { value: 'HYBRID', label: 'Hybrid' },
];

const STATUS_TONES = {
  DRAFT: 'neutral',
  PENDING_APPROVAL: 'warning',
  PUBLISHED: 'success',
  REJECTED: 'danger',
  CLOSED: 'neutral',
};

/** Optional fields an edit may empty. The required ones are never sent as null. */
const CLEARABLE_FIELDS = [
  'responsibilities',
  'requirements',
  'perks',
  'job_category_id',
  'industry_id',
  'location',
  'duration_months',
  'stipend_min',
  'stipend_max',
  'application_deadline',
  'starts_on',
];

const EMPTY = {
  title: '',
  description: '',
  responsibilities: '',
  requirements: '',
  perks: '',
  job_category_id: '',
  industry_id: '',
  location: '',
  work_mode: 'ONSITE',
  duration_months: '',
  positions: 1,
  is_paid: true,
  stipend_min: '',
  stipend_max: '',
  application_deadline: '',
  starts_on: '',
};

/** Create and edit share this screen; `id` in the URL decides which. */
export function CompanyInternshipFormPage() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const { can } = usePermissions();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [skills, setSkills] = useState([]);
  const { needsVerification } = useCompanyVerification();

  const {
    data: internship,
    isPending,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['company-internship', id],
    queryFn: () => companyApi.getInternship(id),
    enabled: isEdit,
  });

  const { data: categories, isPending: categoriesLoading } = useQuery({
    queryKey: ['job-categories'],
    queryFn: () => catalogApi.listJobCategories(),
  });

  const { data: industries, isPending: industriesLoading } = useQuery({
    queryKey: ['industries'],
    queryFn: () => catalogApi.listIndustries(),
  });

  const {
    register,
    handleSubmit,
    reset,
    setError,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({ resolver: zodResolver(internshipSchema), defaultValues: EMPTY });

  // The pickers offer only valid dates: the deadline from today, the start
  // from the day after the deadline. Validation still has the final word.
  const today = localIsoDate(new Date());
  const deadline = watch('application_deadline');
  const isPaid = watch('is_paid');
  const earliestStart = deadline ? localIsoDate(nextDay(deadline)) : today;

  useEffect(() => {
    if (!internship) return;
    reset({
      ...EMPTY,
      title: internship.title ?? '',
      description: internship.description ?? '',
      responsibilities: internship.responsibilities ?? '',
      requirements: internship.requirements ?? '',
      perks: internship.perks ?? '',
      // Without these two the dropdowns always opened empty, so a saved
      // category looked lost.
      job_category_id: internship.job_category_id ?? '',
      industry_id: internship.industry_id ?? '',
      location: internship.location ?? '',
      work_mode: internship.work_mode ?? 'ONSITE',
      duration_months: internship.duration_months ?? '',
      positions: internship.positions ?? 1,
      is_paid: internship.is_paid ?? true,
      stipend_min: internship.stipend_min ?? '',
      stipend_max: internship.stipend_max ?? '',
      application_deadline: internship.application_deadline ?? '',
      starts_on: internship.starts_on ?? '',
    });
    setSkills((internship.required_skills ?? []).map((item) => item.skill.name));
  }, [internship, reset]);

  const save = useMutation({
    mutationFn: (values) =>
      isEdit ? companyApi.updateInternship(id, values) : companyApi.createInternship(values),
    onSuccess: (saved) => {
      // An edit to a live listing sends it back to admin review; say so rather
      // than letting "updated" suggest it is still live.
      toast.success(
        !isEdit
          ? 'Draft created'
          : internship?.status === 'PUBLISHED' && saved?.status === 'PENDING_APPROVAL'
            ? 'Changes saved and sent for review. The listing is back live once Join-Wonder approves it.'
            : 'Internship updated',
      );
      void queryClient.invalidateQueries({ queryKey: ['company-internships'] });
      void queryClient.invalidateQueries({ queryKey: ['company-internship-counts'] });
      // Back to the list either way: a new draft appears there, and an edited
      // listing shows its saved state. Publishing happens from the edit page.
      void queryClient.invalidateQueries({ queryKey: ['company-internship', id] });
      navigate(paths.company.internships, { replace: true });
    },
  });

  const publish = useMutation({
    mutationFn: () => companyApi.publishInternship(id),
    onSuccess: (result) => {
      toast.success(result.message);
      void queryClient.invalidateQueries({ queryKey: ['company-internship', id] });
      void queryClient.invalidateQueries({ queryKey: ['company-internships'] });
      void queryClient.invalidateQueries({ queryKey: ['company-internship-counts'] });
    },
    onError: (publishError) =>
      toast.error(errorMessage(publishError, 'Could not publish the internship')),
  });

  // A select cannot show a value whose option is not there yet, so an edit
  // waits for both lists; otherwise a saved category can render as "Select a
  // category" when the lists arrive after the internship.
  // A new listing cannot be saved until the company is verified, so the form
  // is not offered at all; reached by a bookmark or an old link, this is it.
  if (!isEdit && needsVerification) {
    return (
      <div>
        <PageHeader title="New internship" description="Post a role to students." />
        <EmptyState
          icon={<Icon name="shieldCheck" size={22} />}
          title="Posting opens once your company is verified"
          action={<VerifyToContinueButton />}
        />
      </div>
    );
  }

  if (isEdit && (isPending || categoriesLoading || industriesLoading)) {
    return <LoadingState label="Loading the internship…" />;
  }
  if (isEdit && isError) {
    return <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />;
  }

  async function onSubmit(values) {
    const payload = Object.fromEntries(
      Object.entries(values).filter(([, value]) => value !== '' && value !== undefined),
    );
    // An edit sends a cleared optional field as null, which the API stores as
    // "no value". Dropping it instead -- right for a new draft -- left the old
    // value in place, so clearing a field silently did nothing.
    if (isEdit) {
      for (const field of CLEARABLE_FIELDS) {
        if (payload[field] === undefined) payload[field] = null;
      }
    }

    // Names resolve server-side through the alias table, so a skill typed
    // outside the catalogue still lands on the right one.
    if (skills.length > 0 || isEdit) {
      payload.skills = skills.map((name) => ({ skill_name: name, is_required: true }));
    }

    try {
      await save.mutateAsync(payload);
    } catch (mutationError) {
      if (!applyFieldErrors(mutationError, setError)) {
        toast.error(errorMessage(mutationError, 'Could not save the internship'));
      }
    }
  }

  const canPublish =
    isEdit &&
    can(PERMISSIONS.INTERNSHIP_PUBLISH) &&
    ['DRAFT', 'REJECTED', 'CLOSED'].includes(internship?.status);

  return (
    <div>
      <PageHeader
        title={isEdit ? internship.title : 'New internship'}
        description={
          isEdit
            ? 'Edit the posting, then publish it when it is ready.'
            : 'Save a draft first — nothing is visible to students until you publish.'
        }
        action={
          isEdit && (
            <Badge dot tone={STATUS_TONES[internship.status] ?? 'neutral'}>
              {internship.status.replace('_', ' ')}
            </Badge>
          )
        }
      />

      {internship?.status === 'REJECTED' && internship.rejection_reason && (
        <Alert tone="error" className="mb-6" title="Not approved">
          {internship.rejection_reason}
        </Alert>
      )}

      {internship?.status === 'PUBLISHED' && (
        <Alert tone="info" className="mb-6" title="Changes go back for approval">
          Saving a change takes this listing off the site until Join-Wonder approves it again.
          Applications you have already received are not affected.
        </Alert>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" noValidate>
        <Card>
          <CardHeader
            title="The role"
            description="What the intern will do, and what they need to bring."
          />
          <div className="space-y-4">
            <Input
              label="Title"
              required
              placeholder="Backend Engineering Intern"
              error={errors.title?.message}
              {...register('title')}
            />
            <Textarea
              label="Description"
              required
              rows={6}
              hint="At least 40 characters. What the intern will actually do."
              error={errors.description?.message}
              {...register('description')}
            />
            <Textarea
              label="Responsibilities"
              rows={4}
              hint="One per line. Students see each line as a bullet."
              error={errors.responsibilities?.message}
              {...register('responsibilities')}
            />
            <Textarea
              label="Requirements"
              rows={4}
              hint="One per line. Students see each line as a bullet."
              error={errors.requirements?.message}
              {...register('requirements')}
            />
            <Textarea
              label="Perks"
              rows={3}
              hint="One per line, e.g. mentorship, a learning budget, a certificate."
              error={errors.perks?.message}
              {...register('perks')}
            />
          </div>
        </Card>

        <Card>
          <CardHeader
            title="Classification"
            description="A job category is required before publishing."
          />
          <div className="grid gap-4 sm:grid-cols-2">
            <Select
              label="Job category"
              error={errors.job_category_id?.message}
              {...register('job_category_id')}
            >
              <option value="">Select a category</option>
              {(categories ?? []).map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </Select>
            <Select
              label="Industry"
              error={errors.industry_id?.message}
              {...register('industry_id')}
            >
              <option value="">Select an industry</option>
              {(industries ?? []).map((industry) => (
                <option key={industry.id} value={industry.id}>
                  {industry.name}
                </option>
              ))}
            </Select>
          </div>
          <div className="mt-4">
            <SkillsInput
              hint="Pick from the suggestions, or press Enter to add your own."
              placeholder="Start typing, e.g. Python"
              value={skills}
              onChange={setSkills}
            />
          </div>
        </Card>

        <Card>
          <CardHeader
            title="Where and when"
            description="Location, dates and how many people you are hiring."
          />
          <div className="grid gap-4 sm:grid-cols-2">
            <Select
              label="Work mode"
              required
              error={errors.work_mode?.message}
              {...register('work_mode')}
            >
              {WORK_MODES.map((mode) => (
                <option key={mode.value} value={mode.value}>
                  {mode.label}
                </option>
              ))}
            </Select>
            <Input
              label="Location"
              required
              placeholder="Amsterdam, Netherlands"
              error={errors.location?.message}
              {...register('location')}
            />
            <Input
              label="Duration (months)"
              required
              type="number"
              error={errors.duration_months?.message}
              {...register('duration_months')}
            />
            <Input
              label="Positions"
              required
              type="number"
              error={errors.positions?.message}
              {...register('positions')}
            />
            <Input
              label="Application deadline"
              required
              type="date"
              min={today}
              hint="The last day students can apply."
              error={errors.application_deadline?.message}
              {...register('application_deadline')}
            />
            <Input
              label="Starts on"
              required
              type="date"
              min={earliestStart}
              hint="After the deadline, leaving time to interview and send offers."
              error={errors.starts_on?.message}
              {...register('starts_on')}
            />
          </div>
        </Card>

        <Card>
          <CardHeader
            title="Compensation"
            description="Whether the role is paid, and the stipend range."
          />
          <div className="space-y-4">
            <Checkbox label="This internship is paid" {...register('is_paid')} />
            <div className="grid gap-4 sm:grid-cols-2">
              <Input
                label="Stipend minimum"
                required={isPaid}
                type="number"
                error={errors.stipend_min?.message}
                {...register('stipend_min')}
              />
              <Input
                label="Stipend maximum"
                required={isPaid}
                type="number"
                error={errors.stipend_max?.message}
                {...register('stipend_max')}
              />
            </div>
          </div>
        </Card>

        <div className="flex flex-wrap justify-end gap-2">
          <Button
            variant="secondary"
            type="button"
            onClick={() =>
              navigate(isEdit ? paths.company.internshipDetail(id) : paths.company.internships)
            }
          >
            {isEdit ? 'Back to listing' : 'Back to list'}
          </Button>
          <Button type="submit" isLoading={isSubmitting || save.isPending}>
            {isEdit ? 'Save changes' : 'Save draft'}
          </Button>
          {canPublish &&
            (needsVerification ? (
              <VerifyToContinueButton variant="outline" />
            ) : (
              <Button
                type="button"
                variant="outline"
                isLoading={publish.isPending}
                onClick={() => publish.mutate()}
              >
                Submit for review
              </Button>
            ))}
        </div>
      </form>
    </div>
  );
}

/** "2026-10-07" for a Date, in the viewer's own timezone (what a date input uses). */
function localIsoDate(date) {
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 10);
}

/** The day after an ISO date, as a Date. */
function nextDay(isoDate) {
  const [year, month, day] = isoDate.split('-').map(Number);
  return new Date(year, month - 1, day + 1);
}
